import React, { useRef } from "react";
import type { InputMethod } from "~/components/difyAPITest/difyAPITest.types";
import QuestionChipInput from "~/components/difyAPITest/QuestionChipInput";

type DifyInputMethodSectionProps = {
  inputMethod: InputMethod;
  draft: string;
  questionList: string[];
  selectedFile: File | null;
  setInputMethod: (inputMethod: InputMethod) => void;
  setDraft: (draft: string) => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearCurrentInput: () => void;
  inputRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  focusInput: () => void;
  handlePaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
};

/**
 * 入力方法選択と入力欄を表示するセクション
 * @param props 入力方法セクションのプロパティ
 * @return 画面要素
 */
const DifyInputMethodSection: React.FC<DifyInputMethodSectionProps> = ({
  inputMethod,
  draft,
  questionList,
  selectedFile,
  setInputMethod,
  setDraft,
  handleFileChange,
  onClearCurrentInput,
  inputRef,
  focusInput,
  handlePaste,
  handleKeyDown,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasInputContent =
    inputMethod === "text" ? questionList.length > 0 || draft.length > 0 : selectedFile !== null;

  /**
   * 現在の入力内容をクリアする
   * @return クリア処理の完了
   */
  const handleClearClick = () => {
    if (inputMethod === "file" && fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    onClearCurrentInput();

    if (inputMethod === "text") {
      focusInput();
    }
  };

  return (
    <>
      <div className="dify-registration-row">
        <div className="dify-input-method-group">
          <span className="dify-input-method-label">入力方法</span>
          <div className="dify-input-method-options" role="radiogroup" aria-label="入力方法">
            <label className="dify-input-method-option">
              <input
                type="radio"
                name="inputMethod"
                checked={inputMethod === "file"}
                onChange={() => setInputMethod("file")}
              />
              <span>ファイル</span>
            </label>
            <label className="dify-input-method-option">
              <input
                type="radio"
                name="inputMethod"
                checked={inputMethod === "text"}
                onChange={() => setInputMethod("text")}
              />
              <span>テキスト</span>
            </label>
          </div>
        </div>
      </div>

      {inputMethod === "text" ? (
        <div className="dify-registration-row">
          <div className="dify-input-method-group dify-input-section-group">
            <div className="dify-input-section-header">
              <span className="dify-input-method-label">テキスト</span>
              <button
                type="button"
                className="dify-input-clear-button"
                onClick={handleClearClick}
                disabled={!hasInputContent}
              >
                クリア
              </button>
            </div>
            <div className="dify-input-method-options">
              <QuestionChipInput
                questionList={questionList}
                draft={draft}
                inputRef={inputRef}
                onFocusInput={focusInput}
                onChangeDraft={setDraft}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="dify-registration-row">
          <div className="dify-input-method-group dify-input-section-group">
            <div className="dify-input-section-header">
              <span className="dify-input-method-label">ファイル</span>
              <button
                type="button"
                className="dify-input-clear-button"
                onClick={handleClearClick}
                disabled={!hasInputContent}
              >
                クリア
              </button>
            </div>
            <div className="dify-input-method-options">
              <div className="dify-file-upload-container">
                <input
                  ref={fileInputRef}
                  type="file"
                  id="dify-file-input"
                  className="dify-hidden-file-input"
                  aria-label="ファイルアップロード"
                  accept=".csv"
                  onChange={handleFileChange}
                />
                <label htmlFor="dify-file-input" className="dify-file-upload-area">
                  <span className="material-symbols-rounded dify-file-upload-icon">
                    upload_file
                  </span>
                  <div className="dify-file-upload-text">{selectedFile && selectedFile.name}</div>
                  <div className="dify-file-upload-subtext">
                    {selectedFile ? "選択中のCSVファイル" : "クリックして選択"}
                  </div>
                </label>
                <div className="dify-file-upload-caption">CSVのみ対応</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DifyInputMethodSection;
